import { Component, createEffect, createSignal, JSX, onMount, Show } from 'solid-js'
import Footer from '/web/shared/aifans/Footer'
import Header from '/web/shared/aifans/Header'
import ChatLeftNav from './Chat/ChatLeftNav'
import GenerateImageOptionButton from '/web/shared/aifans/GenerateImage/GenerateImageOptionButton'
import GenerateImageBodyOptions from '/web/shared/aifans/GenerateImage/GenerateImageBodyOptions'
import GenerateImageActionOptions from '/web/shared/aifans/GenerateImage/GenerateImageActionOptions'
import GenerateImageClothingOptions from '/web/shared/aifans/GenerateImage/GenerateImageClothingOptions'
import GenerateImageSceneOptions from '/web/shared/aifans/GenerateImage/GenerateImageSceneOptions'
import GenerateImageAccessoriesOptions from '/web/shared/aifans/GenerateImage/GenerateImageAccessoriesOptions'
import GenerateImageViewOptions from '/web/shared/aifans/GenerateImage/GenerateImageViewOptions'
import { useNavigate, useParams } from '@solidjs/router'
import { AppSchema } from '/common/types'
import { getAssetUrl, setComponentPageTitle } from '/web/shared/util'
import { characterStore, toastStore, userStore } from '/web/store'
import GenerateImageCharacterPicker from '/web/shared/aifans/GenerateImage/GenerateImageCharacterPicker'
import GenerateImageCharacterModal from '/web/shared/aifans/GenerateImage/GenerateImageCharacterModal'
import { imageApi } from '/web/store/data/image'
import HeaderContainer from '/web/shared/aifans/HeaderContainer'
import ChatLeftNavContainer from './Chat/ChatLeftNavContainer'
import Progress from './Progress'
import { Meta, Title } from '@solidjs/meta'

const SectionTitle: Component<{ children: JSX.Element }> = (props) => {
  return <p class="mb-3 text-md text-cosplay-gray-100">{props.children}</p>
}

const TabOption: Component<{ children: JSX.Element; selected?: boolean; onClick: () => void }> = (
  props
) => {
  return (
    <button
      onClick={props.onClick}
      class={`rounded-md px-4 py-1 text-md text-cosplay-gray-100 ${
        props.selected ? 'bg-[#404040]' : ''
      }`}
    >
      {props.children}
    </button>
  )
}

const NumberOption: Component<{
  children: JSX.Element
  selected?: boolean
  premium?: boolean
  onClick: () => void
}> = (props) => {
  return (
    <button
      onClick={props.onClick}
      class={`relative flex h-16 w-16 items-center justify-center rounded-md border text-2xl md:h-20  md:w-20 ${
        props.selected
          ? 'border-white bg-gradient-to-br from-cosplay-purple to-cosplay-blue-200'
          : 'border-cosplay-gray-100/50 bg-[#313131]'
      }`}
    >
      <span>{props.children}</span>
      <Show when={props.premium}>
        <div class="absolute right-0 top-0 h-[15px] rounded-bl-md rounded-tr-md bg-cosplay-purple px-1 text-xxs leading-[15px]">
          Premium
        </div>
      </Show>
    </button>
  )
}

const DEFAULT_PROMPT =
  'Wearing shorts and a crop top shirt, portrait picture, looking at the viewer with a smile'

const GenerateImage: Component = () => {
  const params = useParams()
  const nav = useNavigate()

  const [prompt, setPrompt] = createSignal(DEFAULT_PROMPT)
  const [character, setCharacter] = createSignal<AppSchema.Character | undefined>(undefined)
  const [showCharacterPicker, setShowCharacterPicker] = createSignal<boolean>(false)
  const [characters, setCharacters] = createSignal<AppSchema.Character[]>([])
  const [activeTab, setActiveTab] = createSignal(0)
  const [numberOfImages, setNumberOfImages] = createSignal(1)
  const [image, setImage] = createSignal<string | undefined>(undefined)
  const [generating, setGenerating] = createSignal<boolean>(false)

  // character store
  const chars = characterStore((s) => ({
    list: s.characters.list,
    map: s.characters.list.reduce<Record<string, AppSchema.Character>>(
      (prev, curr) => Object.assign(prev, { [curr._id]: curr }),
      {}
    ),
    loaded: s.characters.loaded,
  }))

  const updatePrompt = (value: string, replacePrompt: boolean) => {
    if (replacePrompt) {
      setPrompt(value)
    } else {
      let p: string = prompt()
      p = p.replace(`, ${value.trim()}`, '')
      p = p.replace(`${value.trim()}, `, '')
      p.replace(',,', '')
      if (p.charAt(p.length - 1) === ',') {
        p = p.substring(0, p.length - 2)
      }

      setPrompt(`${p}, ${value}`)
    }
  }

  const generateImage = async () => {
    const charId = character()?._id

    if (!charId) {
      toastStore.error('Please select a character')
      return
    }

    setGenerating(true)
    setImage(undefined)
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })

    if (charId) {
      try {
        const res = await imageApi.generateFanImage(prompt(), 'summary', charId)

        if (res.error) {
          // yield { waiting: undefined }
          toastStore.error(`Failed to request image: ${res.error}`)
        } else {
          const url = res?.result?.output

          const image = url ? getAssetUrl(url) : ''

          if (userStore().user?.admin && res?.result?.info) {
            console.log(JSON.stringify(JSON.parse(res?.result?.info), null, 2))
          }

          image && setImage(image)
        }
      } catch (error) {
        toastStore.error(`${error}`)
      } finally {
        setGenerating(false)
      }
    }
  }

  createEffect(() => {
    if (!params.id) {
      return
    }

    const char = chars.list.find((c) => c._id === params.id)
    setCharacter(char)
    setCharacters(chars.list)
  })

  onMount(async () => {
    if (!chars.loaded) {
      await characterStore.getCharacters()
    }
    setCharacters(chars.list)
  })

  return (
    <>
      <Title>CosplayFans - Generate Cosplay Images</Title>
      <Meta name="og:title" content="CosplayFans - Generate Cosplay Images" />
      <Meta
        name="description"
        content="Create stunning AI-generated cosplay images on CosplayFans.ai. Choose your style, character, and more with our powerful AI image generator exclusives."
      />
      <Meta
        name="og:description"
        content="Create stunning AI-generated cosplay images on CosplayFans.ai. Choose your style, character, and more with our powerful AI image generator exclusives."
      />
      <Meta
        name="og:image"
        content="https://cdn.cosplayfanscdn.com/assets/char-7abb4c7b-f0ff-4f56-aeca-9ed8ba655811.png"
      />
      <div class="h-full w-full bg-black">
        <div class="h-full w-full">
          <div class="relative w-full">
            <HeaderContainer />
            <ChatLeftNavContainer />
            <div
              id="main"
              class="mb-16 flex flex-1 flex-col pt-14 md:mb-0 md:min-h-[calc(100vh-82px)] md:pt-0 lg:ml-[222px]"
            >
              <div class="flex flex-col md:mt-[82px]">
                <div class="mx-5 mt-4 flex pb-0 md:mt-3 md:px-4 md:pb-0 lg:px-[20px] xl:px-[40px] 2xl:px-[60px]">
                  <span class="font-clash text-[28px] font-bold md:text-[40px]">
                    Generate Image
                  </span>
                </div>
                <div class="grid gap-4 md:grid-cols-2 md:px-4 md:py-5 lg:px-[20px] xl:px-[40px] 2xl:px-[60px]">
                  <div class="flex-1">
                    <Show when={!image()}>
                      <div class="mx-5 flex h-[calc(100vh-440px)] flex-col items-center justify-center rounded-md bg-cosplay-gray-800/90 px-4 py-4 md:h-full md:px-8 md:py-8">
                        <Show when={!generating()}>
                          <img
                            src="/images/generate-image-icon-lg.png"
                            class="mt-1 h-[55px] w-[55px] md:h-[75px] md:w-[75px]"
                          />
                          <p class="mt-2 text-center font-clash text-lg font-bold md:text-xl">
                            Enter a prompt and click Generate
                          </p>
                        </Show>
                        <Show when={generating()}>
                          <Progress completion={false} />
                        </Show>
                      </div>
                    </Show>
                    <Show when={image()}>
                      <div class="relative mx-5 flex h-[calc(100vh-440px)] flex-col items-center justify-center overflow-hidden rounded-md bg-cosplay-gray-800/90 px-4 py-4 md:h-full md:shrink-0 md:grow-0 md:px-8 md:py-8">
                        {image() && (
                          <img
                            class="absolute h-full w-full object-cover"
                            src={getAssetUrl(image() || '')}
                          />
                        )}
                      </div>
                    </Show>
                  </div>
                  <div>
                    <div class="mx-5 mt-4 md:mx-0 md:mt-0">
                      <div class="flex flex-col rounded-md bg-cosplay-gray-800/90 px-4 pb-5 pt-2 md:py-4">
                        <SectionTitle>Choose character</SectionTitle>
                        <GenerateImageCharacterPicker
                          character={character()}
                          onPressChange={() => setShowCharacterPicker(true)}
                        />
                      </div>
                      <div class="mt-4 flex flex-col rounded-md bg-cosplay-gray-800/90 px-4 py-2 md:py-4">
                        <SectionTitle>Enter Prompt</SectionTitle>
                        <textarea
                          class="mb-5 h-24 rounded-md border border-cosplay-gray-100/50 bg-cosplay-gray-800/90 p-3 md:h-28"
                          onInput={(e) => {
                            setPrompt(e.target.value)
                          }}
                          value={prompt()}
                        />
                        <div class="mb-5 flex flex-row flex-wrap gap-4">
                          <GenerateImageOptionButton
                            title="Cute Picture"
                            value="Wearing shorts and a crop top shirt, portrait picture, looking at the viewer with a smile"
                            onClick={updatePrompt}
                            replacePrompt
                          />
                          <GenerateImageOptionButton
                            title="Professional Picture"
                            value="Wearing an office suit, portrait picture, in the office, looking at the viewer"
                            onClick={updatePrompt}
                            replacePrompt
                          />
                          <GenerateImageOptionButton
                            title="Walking on the beach"
                            value="Wearing a swimsuit, walking on the beach by the sea waves and palm trees"
                            onClick={updatePrompt}
                            replacePrompt
                          />
                          <GenerateImageOptionButton
                            title="Out for dinner"
                            value="Wearing a pencil dress, sitting at a table in a fancy restaurant at night with dim lights"
                            onClick={updatePrompt}
                            replacePrompt
                          />
                        </div>
                        <SectionTitle>Customize your image</SectionTitle>
                        <div class="mb-5 flex flex-row flex-wrap gap-1 rounded-md bg-[#313131] p-1 2xl:justify-between">
                          <TabOption selected={activeTab() === 0} onClick={() => setActiveTab(0)}>
                            Action
                          </TabOption>
                          <TabOption selected={activeTab() === 1} onClick={() => setActiveTab(1)}>
                            Body
                          </TabOption>
                          <TabOption selected={activeTab() === 2} onClick={() => setActiveTab(2)}>
                            Clothing
                          </TabOption>
                          <TabOption selected={activeTab() === 3} onClick={() => setActiveTab(3)}>
                            Scene
                          </TabOption>
                          <TabOption selected={activeTab() === 4} onClick={() => setActiveTab(4)}>
                            Accessories
                          </TabOption>
                          <TabOption selected={activeTab() === 5} onClick={() => setActiveTab(5)}>
                            View
                          </TabOption>
                        </div>
                        <Show when={activeTab() === 0}>
                          <GenerateImageActionOptions updatePrompt={updatePrompt} />
                        </Show>
                        <Show when={activeTab() === 1}>
                          <GenerateImageBodyOptions updatePrompt={updatePrompt} />
                        </Show>
                        <Show when={activeTab() === 2}>
                          <GenerateImageClothingOptions updatePrompt={updatePrompt} />
                        </Show>
                        <Show when={activeTab() === 3}>
                          <GenerateImageSceneOptions updatePrompt={updatePrompt} />
                        </Show>
                        <Show when={activeTab() === 4}>
                          <GenerateImageAccessoriesOptions updatePrompt={updatePrompt} />
                        </Show>
                        <Show when={activeTab() === 5}>
                          <GenerateImageViewOptions updatePrompt={updatePrompt} />
                        </Show>
                        <div class="mt-4 flex flex-col">
                          <Show when={false}>
                            <SectionTitle>Number of images</SectionTitle>
                            <div class="flex flex-row justify-between gap-3 md:justify-normal">
                              <NumberOption
                                selected={numberOfImages() === 1}
                                onClick={() => setNumberOfImages(1)}
                              >
                                1
                              </NumberOption>
                              <NumberOption
                                selected={numberOfImages() === 2}
                                onClick={() => setNumberOfImages(2)}
                                premium
                              >
                                2
                              </NumberOption>
                              <NumberOption
                                selected={numberOfImages() === 3}
                                onClick={() => setNumberOfImages(3)}
                                premium
                              >
                                3
                              </NumberOption>
                              <NumberOption
                                selected={numberOfImages() === 4}
                                onClick={() => setNumberOfImages(4)}
                                premium
                              >
                                4
                              </NumberOption>
                            </div>
                          </Show>
                          <div class="mt-4">
                            <Show when={generating()}>
                              <div
                                class={`flex h-9 w-full items-center justify-center gap-2 rounded-md border border-white bg-gradient-to-r from-[#B14DFF] to-[#4A72FF] pl-3 pr-[17px]  font-clash text-xl font-semibold `}
                                role="status"
                              >
                                <svg
                                  aria-hidden="true"
                                  class="inline h-5 w-5 animate-spin fill-purple-600 text-gray-200"
                                  viewBox="0 0 100 101"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                    fill="currentFill"
                                  />
                                </svg>
                                <span class="sr-only">Loading...</span>
                              </div>
                            </Show>
                            <Show when={!generating()}>
                              <button
                                onClick={() => generateImage()}
                                class={`flex h-9 w-full items-center justify-center gap-2 rounded-md border border-white bg-gradient-to-r from-[#B14DFF] to-[#4A72FF] pl-3 pr-[17px]  font-clash text-xl font-semibold `}
                              >
                                <img src={'/images/generate-button-icon.png'} class="w-5" />
                                <span class="text-sm leading-4">GENERATE</span>
                                <div class="flex flex-row items-center justify-center gap-2 rounded-md bg-cosplay-purple px-2 py-1">
                                  <span class="text-sm leading-4">2</span>
                                  <img src={'/images/coins-stack.png'} class="h-5 w-5" />
                                </div>
                              </button>
                            </Show>
                            <p class="mt-4 text-sm leading-3">
                              Generated Image will be saved to Gallery
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Footer />
              </div>
            </div>
          </div>
        </div>
      </div>

      <GenerateImageCharacterModal
        show={showCharacterPicker()}
        hide={() => setShowCharacterPicker(false)}
        characters={characters()}
        selectedCharacter={character()}
        onSelect={(c) => {
          setCharacter(c)
          setShowCharacterPicker(false)
          nav(`/generate-image/${c._id}`)
        }}
      />
    </>
  )
}

export default GenerateImage
